import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Clock,
  Gauge,
  Hash,
  Timer,
  Zap,
} from 'lucide-react'
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  DatePicker,
  Radio,
  Dropdown,
  Checkbox,
} from '@arco-design/web-react'
import { IconRefresh, IconSearch, IconSettings } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import { useRole } from '@/hooks/useRole'
import StatsSection, { type MetricItem } from '@/pages/Dashboard/StatsSection'
import LogDetailDrawer from '@/components/log/LogDetailDrawer'
import { useSelfLogs, useSelfLogStats, useAllLogs, useAllLogStats } from '@/hooks/useLogs'
import type { LogListParams, LogStatParams } from '@/api/logs'
import {
  renderModelTag,
  renderTimestamp,
  renderDurationMs,
  renderLogStatus,
  renderErrorStage,
  renderStreamMode,
} from '@/utils/render'
import { renderQuota, formatNumberCompact } from '@/utils/quota'
import { LOG_TYPE } from '@/utils/constants'
import type { LogEntry } from '@/types'

interface Filters {
  type: number
  modelName?: string
  tokenName?: string
  userId?: string
  tokenId?: string
  requestId?: string
  upstreamRequestId?: string
  username?: string
  channel?: number
  group?: string
  status?: string
  errorStage?: string
  stream?: '' | 'true' | 'false'
  ip?: string
  minTotalMs?: string
  minUpstreamMs?: string
  minTtftMs?: string
  range?: [number, number]
}

const now = new Date()
const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
const defaultRange: [number, number] = [
  startOfToday - 7 * 24 * 3600 * 1000,
  startOfToday + 24 * 3600 * 1000 - 1,
]

const ERROR_STAGES = [
  'auth',
  'rate_limit',
  'route',
  'pre_billing',
  'upstream',
  'stream',
  'settle_billing',
  'client_abort',
  'system',
]

const LOG_STATUSES = ['success', 'error', 'refund', 'cancel', 'timeout', 'retry']

function buildListParams(
  filters: Filters,
  page: number,
  pageSize: number,
  scope: 'self' | 'all',
): LogListParams {
  const [start, end] = filters.range || defaultRange
  const params: LogListParams = {
    page,
    page_size: pageSize,
    type: filters.type || undefined,
    model_name: filters.modelName,
    token_name: filters.tokenName,
    request_id: filters.requestId,
    upstream_request_id: filters.upstreamRequestId,
    group: filters.group,
    status: filters.status,
    error_stage: filters.errorStage,
    ip: filters.ip,
    start_timestamp: Math.floor(start / 1000),
    end_timestamp: Math.floor(end / 1000),
  }
  if (scope === 'all') params.username = filters.username
  const uid = Number(filters.userId)
  if (uid > 0) params.user_id = uid
  const tid = Number(filters.tokenId)
  if (tid > 0) params.token_id = tid
  if (filters.channel) params.channel = filters.channel
  if (filters.stream === 'true') params.stream = true
  if (filters.stream === 'false') params.stream = false
  const minTotal = Number(filters.minTotalMs)
  if (minTotal > 0) params.min_total_ms = minTotal
  const minUpstream = Number(filters.minUpstreamMs)
  if (minUpstream > 0) params.min_upstream_ms = minUpstream
  const minTtft = Number(filters.minTtftMs)
  if (minTtft > 0) params.min_ttft_ms = minTtft
  return params
}

function buildStatParams(filters: Filters, scope: 'self' | 'all'): LogStatParams {
  const { page: _p, page_size: _ps, ...rest } = buildListParams(filters, 1, 1, scope)
  return rest
}

export default function LogPage() {
  const { t } = useTranslation()
  const { isAdmin } = useRole()
  const [searchParams] = useSearchParams()
  const [scope, setScope] = useState<'self' | 'all'>(isAdmin ? 'all' : 'self')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const initialRequestId = searchParams.get('request_id')?.trim() || undefined
  const [filters, setFilters] = useState<Filters>({
    type: LOG_TYPE.ALL,
    range: defaultRange,
    requestId: initialRequestId,
  })
  const [appliedFilters, setAppliedFilters] = useState<Filters>(filters)
  const [detailRow, setDetailRow] = useState<LogEntry | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  useEffect(() => {
    const rid = searchParams.get('request_id')?.trim()
    if (!rid) return
    setFilters((prev) => ({ ...prev, requestId: rid }))
    setAppliedFilters((prev) => ({ ...prev, requestId: rid }))
    setPage(1)
  }, [searchParams])

  const listParams = useMemo(
    () => buildListParams(appliedFilters, page, pageSize, scope),
    [appliedFilters, page, pageSize, scope],
  )
  const statParams = useMemo(() => buildStatParams(appliedFilters, scope), [appliedFilters, scope])

  const selfLogs = useSelfLogs(listParams, scope === 'self')
  const selfStats = useSelfLogStats(statParams, scope === 'self')
  const allLogs = useAllLogs(listParams, scope === 'all')
  const allStats = useAllLogStats(statParams, scope === 'all')

  const logsResult = scope === 'self' ? selfLogs : allLogs
  const statsResult = scope === 'self' ? selfStats : allStats
  const stats = statsResult.data

  const logTypeOptions = useMemo(
    () =>
      Object.entries({
        0: t('全部'),
        1: t('充值'),
        2: t('消费'),
        3: t('管理'),
        4: t('系统'),
        5: t('错误'),
        6: t('退款'),
      }).map(([v, label]) => ({ value: Number(v), label })),
    [t],
  )

  const allColumns = useMemo(
    () => [
      { key: 'createdAt', title: t('tt.log.time'), dataIndex: 'createdAt', width: 160, render: renderTimestamp },
      { key: 'status', title: t('tt.log.status'), dataIndex: 'status', width: 96, render: (v?: string) => renderLogStatus(v) },
      { key: 'modelName', title: t('tt.log.model'), dataIndex: 'modelName', width: 160, render: (v?: string) => (v ? renderModelTag(v) : '-') },
      { key: 'username', title: t('tt.log.user'), dataIndex: 'username', width: 120, adminOnly: true, scopeAllOnly: true },
      { key: 'tokenName', title: t('tt.log.token'), dataIndex: 'tokenName', width: 140, render: (v?: string) => v || '-' },
      { key: 'promptTokens', title: t('tt.log.promptTokens'), dataIndex: 'promptTokens', width: 96, align: 'right' as const, render: (v?: number) => <span className="tabular-nums text-xs">{v != null ? formatNumberCompact(v) : '-'}</span> },
      { key: 'completionTokens', title: t('tt.log.completionTokens'), dataIndex: 'completionTokens', width: 96, align: 'right' as const, render: (v?: number) => <span className="tabular-nums text-xs">{v != null ? formatNumberCompact(v) : '-'}</span> },
      { key: 'quota', title: t('tt.log.quota'), dataIndex: 'quota', width: 110, render: (v: number) => renderQuota(v) },
      { key: 'totalMs', title: t('tt.log.totalMs'), dataIndex: 'totalMs', width: 100, render: (v?: number) => renderDurationMs(v) },
      { key: 'clientTtftMs', title: t('tt.log.clientTtft'), dataIndex: 'clientTtftMs', width: 100, render: (v?: number) => renderDurationMs(v) },
      { key: 'upstreamTotalMs', title: t('tt.log.upstreamMs'), dataIndex: 'upstreamTotalMs', width: 110, adminOnly: true, render: (v?: number) => renderDurationMs(v) },
      { key: 'isStream', title: t('tt.log.returnMode'), dataIndex: 'isStream', width: 100, render: (_: boolean, row: LogEntry) => renderStreamMode(row.stream ?? row.isStream) },
      { key: 'errorStage', title: t('tt.log.errorStage'), dataIndex: 'errorStage', width: 110, render: (v?: string) => renderErrorStage(v) },
      { key: 'requestId', title: t('tt.log.requestId'), dataIndex: 'requestId', width: 180, render: (v?: string) => <code className="text-xs font-mono">{v || '-'}</code> },
      { key: 'channelName', title: t('tt.log.channel'), dataIndex: 'channelName', width: 120, adminOnly: true, render: (v?: string) => v || '-' },
      {
        key: 'actions',
        title: t('tt.log.actions'),
        width: 88,
        fixed: 'right' as const,
        render: (_: unknown, row: LogEntry) => (
          <Button type="text" size="small" onClick={() => { setDetailRow(row); setDetailOpen(true) }}>
            {t('tt.log.detail')}
          </Button>
        ),
      },
    ],
    [t],
  )

  const defaultVisible = useMemo(
    () =>
      allColumns
        .filter((c) => !c.adminOnly || isAdmin)
        .filter((c) => !c.scopeAllOnly || scope === 'all')
        .map((c) => c.key),
    [allColumns, isAdmin, scope],
  )
  const [visibleColumns, setVisibleColumns] = useState<string[]>(defaultVisible)

  useEffect(() => {
    const allowed = allColumns
      .filter((c) => !c.adminOnly || isAdmin)
      .filter((c) => !c.scopeAllOnly || scope === 'all')
      .map((c) => c.key)
    setVisibleColumns((prev) => {
      const allowedSet = new Set(allowed)
      const filtered = prev.filter((k) => allowedSet.has(k))
      const next = [...filtered]
      for (const k of allowed) {
        if (!next.includes(k)) next.push(k)
      }
      return next
    })
  }, [scope, allColumns, isAdmin])

  const columns = allColumns
    .filter((c) => !c.adminOnly || isAdmin)
    .filter((c) => !c.scopeAllOnly || scope === 'all')
    .filter((c) => visibleColumns.includes(c.key))

  const logStatMetrics: MetricItem[] = useMemo(
    () => {
      const loading = statsResult.isLoading
      const base: MetricItem[] = [
      { key: 'total', title: t('tt.log.statTotal'), value: formatNumberCompact(stats?.total_requests), icon: Activity, tone: 'indigo', loading },
      { key: 'successRate', title: t('tt.log.statSuccessRate'), value: `${stats?.success_rate ?? 0}%`, icon: Gauge, tone: 'emerald', loading },
      { key: 'errorRate', title: t('tt.log.statErrorRate'), value: stats?.total_requests ? `${Math.round(((stats.error_count ?? 0) / stats.total_requests) * 10000) / 100}%` : '0%', icon: AlertTriangle, tone: 'amber', loading },
      { key: 'prompt', title: t('tt.log.promptTokens'), value: formatNumberCompact(stats?.prompt_tokens), icon: Hash, tone: 'cyan', loading },
      { key: 'completion', title: t('tt.log.completionTokens'), value: formatNumberCompact(stats?.completion_tokens), icon: BarChart3, tone: 'slate', loading },
      { key: 'avgTotal', title: t('tt.log.statAvgTotal'), value: String(stats?.avg_total_ms ? `${stats.avg_total_ms} ms` : '-'), icon: Clock, tone: 'indigo', loading },
      isAdmin
        ? { key: 'p95Upstream', title: t('tt.log.statP95Upstream'), value: String(stats?.p95_upstream_ms ? `${stats.p95_upstream_ms} ms` : '-'), icon: Timer, tone: 'amber', loading }
        : { key: 'p95Total', title: t('tt.log.statP95Total'), value: String(stats?.p95_total_ms ? `${stats.p95_total_ms} ms` : '-'), icon: Timer, tone: 'amber', loading },
      { key: 'avgTtft', title: t('tt.log.statAvgTtft'), value: String(stats?.avg_ttft_ms ? `${stats.avg_ttft_ms} ms` : '-'), icon: Zap, tone: 'cyan', loading },
    ]
      return base
    },
    [stats, statsResult.isLoading, isAdmin, t],
  )

  const applyFilters = () => {
    setAppliedFilters(filters)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <Card className="tt-card !rounded-2xl" bordered={false}>
        <StatsSection metrics={logStatMetrics} />
      </Card>

      <Card className="tt-card !rounded-2xl" bordered={false} bodyStyle={{ padding: '12px 16px' }}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
          {isAdmin ? (
            <Radio.Group
              type="button"
              className="saas-range-group shrink-0"
              value={scope}
              onChange={(v) => { setScope(v); setPage(1) }}
              options={[
                { label: t('tt.log.scopeSelf'), value: 'self' },
                { label: t('tt.log.scopeAll'), value: 'all' },
              ]}
            />
          ) : null}
          <DatePicker.RangePicker
            showTime
            value={filters.range}
            onChange={(v) => setFilters((f) => ({ ...f, range: v ? [v[0] as unknown as number, v[1] as unknown as number] : defaultRange }))}
            className="w-full sm:w-auto"
          />
          <Select placeholder={t('tt.log.type')} value={filters.type} onChange={(v) => setFilters((f) => ({ ...f, type: v }))} className="w-full sm:w-[120px]" options={logTypeOptions} />
          <Select placeholder={t('tt.log.status')} allowClear value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} className="w-full sm:w-[120px]" options={LOG_STATUSES.map((s) => ({ label: s, value: s }))} />
          <Select placeholder={t('tt.log.errorStage')} allowClear value={filters.errorStage} onChange={(v) => setFilters((f) => ({ ...f, errorStage: v }))} className="w-full sm:w-[140px]" options={ERROR_STAGES.map((s) => ({ label: s, value: s }))} />
          <Input placeholder={t('tt.log.modelPlaceholder')} value={filters.modelName} onChange={(v) => setFilters((f) => ({ ...f, modelName: v }))} className="w-full sm:w-[150px]" allowClear />
          {scope === 'all' && <Input placeholder={t('tt.log.usernamePlaceholder')} value={filters.username} onChange={(v) => setFilters((f) => ({ ...f, username: v }))} className="w-full sm:w-[150px]" allowClear />}
          {isAdmin && scope === 'all' && <Input placeholder="user_id" value={filters.userId} onChange={(v) => setFilters((f) => ({ ...f, userId: v }))} className="w-full sm:w-[100px]" allowClear />}
          {isAdmin && <Input placeholder="token_id" value={filters.tokenId} onChange={(v) => setFilters((f) => ({ ...f, tokenId: v }))} className="w-full sm:w-[100px]" allowClear />}
          <Input placeholder={t('tt.log.tokenPlaceholder')} value={filters.tokenName} onChange={(v) => setFilters((f) => ({ ...f, tokenName: v }))} className="w-full sm:w-[150px]" allowClear />
          {isAdmin && <Input placeholder={t('tt.log.channelPlaceholder')} value={filters.channel ? String(filters.channel) : ''} onChange={(v) => setFilters((f) => ({ ...f, channel: v ? Number(v) : undefined }))} className="w-full sm:w-[120px]" allowClear />}
          <Input placeholder={t('tt.log.groupPlaceholder')} value={filters.group} onChange={(v) => setFilters((f) => ({ ...f, group: v }))} className="w-full sm:w-[120px]" allowClear />
          <Input placeholder={t('tt.log.requestIdPlaceholder')} value={filters.requestId} onChange={(v) => setFilters((f) => ({ ...f, requestId: v }))} className="w-full sm:w-[200px]" allowClear />
          {isAdmin && <Input placeholder={t('tt.log.upstreamRequestIdPlaceholder')} value={filters.upstreamRequestId} onChange={(v) => setFilters((f) => ({ ...f, upstreamRequestId: v }))} className="w-full sm:w-[200px]" allowClear />}
          <Input placeholder={t('tt.log.ipPlaceholder')} value={filters.ip} onChange={(v) => setFilters((f) => ({ ...f, ip: v }))} className="w-full sm:w-[140px]" allowClear />
          <Select placeholder={t('tt.log.returnMode')} allowClear value={filters.stream} onChange={(v) => setFilters((f) => ({ ...f, stream: v as Filters['stream'] }))} className="w-full sm:w-[120px]" options={[{ label: t('tt.log.stream'), value: 'true' }, { label: t('tt.log.nonStream'), value: 'false' }]} />
          <Input placeholder={t('tt.log.minTotalMs')} value={filters.minTotalMs} onChange={(v) => setFilters((f) => ({ ...f, minTotalMs: v }))} className="w-full sm:w-[120px]" allowClear />
          {isAdmin && <Input placeholder={t('tt.log.minUpstreamMs')} value={filters.minUpstreamMs} onChange={(v) => setFilters((f) => ({ ...f, minUpstreamMs: v }))} className="w-full sm:w-[130px]" allowClear />}
          <Input placeholder={t('tt.log.minTtftMs')} value={filters.minTtftMs} onChange={(v) => setFilters((f) => ({ ...f, minTtftMs: v }))} className="w-full sm:w-[120px]" allowClear />
          <Button icon={<IconSearch />} type="primary" onClick={applyFilters}>{t('查询')}</Button>
          <Button icon={<IconRefresh />} onClick={() => logsResult.refetch()}>{t('刷新')}</Button>
          <Dropdown
            trigger="click"
            droplist={
              <div className="bg-white rounded-md shadow-md p-3 border border-[#F3F4F6] w-[240px]">
                <div className="text-xs text-gray-500 mb-2">{t('tt.log.selectColumns')}</div>
                <Checkbox.Group
                  value={visibleColumns}
                  onChange={(v) => setVisibleColumns(v as string[])}
                  direction="vertical"
                  options={allColumns
                    .filter((c) => !c.adminOnly || isAdmin)
                    .filter((c) => !c.scopeAllOnly || scope === 'all')
                    .filter((c) => c.key !== 'actions')
                    .map((c) => ({ label: c.title, value: c.key }))}
                />
              </div>
            }
          >
            <Button icon={<IconSettings />}>{t('列设置')}</Button>
          </Dropdown>
        </div>
      </Card>

      <Card className="tt-card !rounded-2xl" bordered={false}>
        <Table
          className="log-list-table"
          rowKey="id"
          loading={logsResult.isLoading}
          columns={columns}
          data={logsResult.data?.items || []}
          scroll={{ x: 1600 }}
          pagination={{
            current: page,
            pageSize,
            total: logsResult.data?.total || 0,
            showTotal: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) },
          }}
        />
      </Card>

      <LogDetailDrawer
        visible={detailOpen}
        record={detailRow}
        isAdmin={isAdmin}
        showUser={scope === 'all'}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  )
}
